import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getDownloadURL, listAll, ref as storageRef } from "firebase/storage";
import { storage } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";
import { theme } from "../../styles/theme";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Label, ErrorMessage } from "../ui/Input";
import { remixTemplates } from "../../constants/remixTemplates";

const Layout = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  border: ${theme.borders.base};
  font-family: inherit;
  resize: vertical;
`;

const TemplateRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  overflow-x: auto;
  padding: ${theme.spacing["2xs"]} 0;
`;

const TemplateButton = styled.button`
  border: ${theme.borders.base};
  background: ${theme.colors.background.tertiary};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.pill};
  padding: ${theme.spacing["2xs"]} ${theme.spacing.sm};
  cursor: pointer;
  white-space: nowrap;
  font-size: ${theme.typography.fontSize.sm};
`;

const ActorRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  overflow-x: auto;
  padding: ${theme.spacing.xs} 0;
  flex-wrap: nowrap;
`;

const ActorCard = styled.button`
  border: ${theme.borders.base};
  background: ${({ selected }) =>
    selected ? theme.colors.background.secondary : theme.colors.background.canvas};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing["3xs"]};
  cursor: pointer;
  position: relative;
  height: 100px;
  min-height: 100px;
  width: auto;
  min-width: 60px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: ${({ selected }) =>
    selected ? `0 0 0 3px ${theme.colors.accent.primary}` : "none"};
`;

const ActorImg = styled.img`
  height: 100%;
  width: auto;
  object-fit: contain;
  display: block;
`;

const NoneCard = styled(ActorCard)`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  flex-direction: column;
  gap: ${theme.spacing["3xs"]};
`;

function InitialRemixDialog({ isOpen, seed, slice, onSubmit, onClose, loading }) {
  const { currentUser } = useAuth();
  const [extraPrompt, setExtraPrompt] = useState("");
  const [actors, setActors] = useState([]);
  const [selectedActor, setSelectedActor] = useState("");
  const [cleanModel, setCleanModel] = useState("ultra");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && currentUser) {
      setExtraPrompt("");
      setSelectedActor("");
      setCleanModel("ultra");
      setError("");
      const loadActors = async () => {
        try {
          const baseRef = storageRef(storage, `user_data/${currentUser.uid}/actors`);
          const res = await listAll(baseRef);
          const urls = await Promise.all(res.items.map((item) => getDownloadURL(item)));
          setActors(urls);
        } catch (e) {
          console.warn("Failed to load actors", e);
          setActors([]);
        }
      };
      loadActors();
    }
  }, [isOpen, currentUser]);

  if (!isOpen || !seed || !slice) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    onSubmit?.({
      extraPrompt: extraPrompt.trim(),
      actorUrl: selectedActor || null,
      cleanModel,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={loading ? undefined : onClose} title="Refine initial image">
      <Layout onSubmit={handleSubmit}>
        <div>
          <Label htmlFor="initial-extra">Extra instructions (optional)</Label>
          <TemplateRow>
            {remixTemplates.map((tpl) => (
              <TemplateButton
                key={tpl.name}
                type="button"
                onClick={() =>
                  setExtraPrompt((prev) =>
                    prev ? `${prev}\n\n${tpl.prompt}` : tpl.prompt
                  )
                }
                disabled={loading}
                title={tpl.prompt}
              >
                {tpl.name}
              </TemplateButton>
            ))}
          </TemplateRow>
          <TextArea
            id="initial-extra"
            value={extraPrompt}
            onChange={(e) => setExtraPrompt(e.target.value)}
            disabled={loading}
            placeholder="Add any extra guidance for the initial image cleanup..."
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing["2xs"] }}>
          <Label htmlFor="clean-model">Cleanup Model</Label>
          <select
            id="clean-model"
            value={cleanModel}
            onChange={(e) => setCleanModel(e.target.value)}
            disabled={loading}
            style={{
              padding: theme.spacing.sm,
              borderRadius: theme.borderRadius.md,
              border: theme.borders.base,
              width: "100%",
            }}
          >
            <option value="ultra">Ultra (nano banana pro)</option>
            <option value="fast">Fast (seedream 4.5)</option>
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing["2xs"] }}>
          <Label>Actor image (optional)</Label>
          <ActorRow>
            <NoneCard
              type="button"
              onClick={() => setSelectedActor("")}
              selected={!selectedActor}
              disabled={loading}
            >
              <span style={{ fontSize: theme.typography.fontSize.lg }}>◎</span>
              <span style={{ fontSize: theme.typography.fontSize.sm }}>None</span>
            </NoneCard>
            {actors.map((url) => (
              <ActorCard
                key={url}
                type="button"
                onClick={() => setSelectedActor(url === selectedActor ? "" : url)}
                selected={url === selectedActor}
                disabled={loading}
              >
                <ActorImg src={url} alt="Actor" />
              </ActorCard>
            ))}
          </ActorRow>
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: theme.spacing.sm }}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Create project"}
          </Button>
        </div>
      </Layout>
    </Modal>
  );
}

export default InitialRemixDialog;

